import React from 'react';
import { BrandFormModal } from '@/pages/brands/components/brandFormModal';
import { useCreateBrand } from '@/pages/brands/useCreateBrand';
import { PATH } from '@/router/PATH';
import type { ActionSurfaceProps } from '../../controlCenterSurfaces';

/**
 * "Add brand", opened from the launcher — the brands page's **own** drawer.
 *
 * Five of the six catalogue adapters are this file with different nouns, and
 * that is the point: the shape is the seam, not a special case. Each one holds
 * three feature-shaped facts and nothing else — which form, how it is created,
 * and what a save should say and link to.
 *
 * The one difference from `AddEmployeeSurface` is where the write lives. The
 * employee drawer is self-served and owns its mutation; these six forms are
 * prop-driven Views whose page hook owns it, so the adapter calls the shared
 * `useCreateBrand` — the same function the brands page calls. There is one
 * definition of how a brand is created, and cache invalidation comes with it:
 * `useCreateBrandMutation` invalidates the list, so following the confirmation's
 * link shows the new row with no handoff.
 *
 * The failure branch keeps the drawer open with an inline error, exactly as the
 * owning page does. `onDone` fires only on a real success, so a cancel confirms
 * nothing.
 */
export const AddBrandSurface: React.FC<ActionSurfaceProps> = ({
    onClose,
    onDone,
}) => {
    const [error, setError] = React.useState<string | null>(null);
    const { create, isCreating } = useCreateBrand();

    return (
        <BrandFormModal
            error={error}
            initialValues={null}
            isOpen
            isSubmitting={isCreating}
            // Always a create: the launcher adds, the page edits.
            mode="create"
            onClose={onClose}
            onSubmit={async (input) => {
                setError(null);
                const created = await create(input);
                if (!created.ok) {
                    setError(created.message);
                    return;
                }
                onDone({
                    messageKey: 'controlCenter.outcome.brandAdded',
                    messageParams: { name: input.name },
                    to: PATH.BRANDS,
                    linkKey: 'controlCenter.outcome.openBrands',
                });
            }}
        />
    );
};
